import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { addEdge, Background, Controls, ReactFlowProvider, useEdgesState, useNodesState } from 'reactflow';
import 'reactflow/dist/style.css';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useSnackbar } from 'notistack';
import { Tooltip, Fab } from '@mui/material';
import Table1Node from './llm-mapper-component/Table1Node';
import Table2Node from './llm-mapper-component/Table2Node';
import AddColumnForm from './llm-mapper-component/AddColumnForm';

const LlmMapperFlow = ({
  fetchLlmMapperDataByAI,
  setFormField,
  formField,
  sourceColumns,
  targetColumns,
  disableForm,
  onMappingChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const [reactFlowMapping, setReactFlowMapping] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeColorIndex, setEdgeColorIndex] = useState({});
  const [doubleClickTimeout, setDoubleClickTimeout] = useState(null);
  const [lastDestY, setLastDestY] = useState(0);
  const [maxWidthLength, setMaxWidthLength] = useState(0);
  const [nextNodeId, setNextNodeId] = useState(0);
  const [columnForm, setColumnForm] = useState({
    column_name: '',
    data_type: '',
  });
  const [isOpenAddNodeFormModal, setIsOpenAddNodeFormModal] = useState(false);

  useEffect(() => {
    onMappingChange?.(edges.length > 0);
  }, [edges]);

  const handleAddNode = () => {
    setColumnForm({
      column_name: '',
      data_type: '',
    });
    setIsOpenAddNodeFormModal(true);
  };

  const handleAddNodeFormClose = () => {
    setIsOpenAddNodeFormModal(false); // Close the modal
  };

  const handleSaveColumnForm = () => {
    if (columnForm.column_name.trim() === '' || columnForm.data_type === '') {
      enqueueSnackbar('Column Name and Data Type are required!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }
    if (columnForm.column_name.length > 15) {
      enqueueSnackbar('Column Name should not exceed 15 characters!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    const newNodeId = `target-node-${columnForm.column_name}`; // Generate new node ID
    const newNode = {
      id: newNodeId,
      position: { x: 400, y: nextNodeId * 30 + lastDestY },
      data: {
        label: columnForm.column_name,
        datatype: columnForm.data_type,
        width: maxWidthLength,
        formField,
        setFormField,
        sourceColumns,
        targetColumns,
        reactFlowMapping,
      },
      type: 'target',
      draggable: false,
    };

    const updatedNodes = nodes.map((node) => {
      if (node.id === 'table2-title2') {
        return {
          ...node,
          position: { ...node.position, y: nextNodeId * 30 + 30 + lastDestY }, // Adjust y position based on lastDestY
        };
      }
      return node;
    });
    setNodes([...updatedNodes, newNode]);

    setNextNodeId(nextNodeId + 1); // Increment the next node ID for future width calculation
    // setLastDestY(lastDestY + 30);

    handleAddNodeFormClose();
  };

  const nodeTypes = useMemo(
    () => ({
      source: Table1Node,
      target: Table2Node,
      title: ({ data }) => (
        <div
          style={{
            fontWeight: 700,
            fontSize: 10,
            marginLeft: `${data.label === 'Destination Table' && '8px'}`,
          }}
        >
          {data.label}
        </div>
      ),
      addNode: ({ data: { width } }) => (
        <>
          <div
            style={{
              width: `${width * 9 + 50}px`,
              display: 'flex',
              justifyContent: 'center',
              padding: '2px 16px 0 0',
            }}
          >
            <Tooltip title="Add Column in Destination">
              <AddCircleIcon
                onClick={() => handleAddNode()}
                style={{ fontSize: 'medium', color: 'gray', cursor: 'pointer' }}
              />
            </Tooltip>
          </div>
        </>
      ),
    }),
    []
  );

  const edgeColors = ['#888', '#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#FFC300'];

  const onConnect = (params) => {
    setEdges((eds) => addEdge(params, eds));

    // Find the source and target nodes from the nodes state
    const sourceNode = nodes.find((node) => node.id === params.source);
    const targetNode = nodes.find((node) => node.id === params.target);

    if (sourceNode && targetNode) {
      const sourceLabel = sourceNode.data.label;
      const targetLabel = targetNode.data.label;

      let sourceNodeId = reactFlowMapping.find((mapping) => mapping.source.name === sourceLabel)?.sourceNodeId;
      let destNodeId = reactFlowMapping.find((mapping) => mapping.destination.name === targetLabel)?.destNodeId;
      if (!sourceNodeId) {
        sourceNodeId = `source-node-${sourceLabel}`;
      }
      if (!destNodeId) {
        destNodeId = `target-node-${targetLabel}`;
      }

      addNewMapping({
        sourceNodeId,
        source: { name: sourceLabel, type: sourceNode.data.datatype },
        destNodeId,
        destination: { name: targetLabel, type: targetNode.data.datatype },
      });
    }
  };

  const addNewMapping = (newMapping) => {
    const reactFlowMappingExist = reactFlowMapping.find(
      (mapping) => mapping.sourceNodeId === newMapping.sourceNodeId && mapping.destNodeId === newMapping.destNodeId
    );
    if (!reactFlowMappingExist) {
      reactFlowMapping.push(newMapping);
      setReactFlowMapping(reactFlowMapping);
      formField = {
        ...formField,
        mappings: formField.mappings.map((mapping) => {
          if (mapping.destination_column.name === newMapping.destination.name) {
            const uniqueSourceColumns = [...mapping.source_columns, newMapping.source].filter(
              (value, index, self) => index === self.findIndex((v) => v.name === value.name)
            );
            return { ...mapping, source_columns: uniqueSourceColumns };
          }
          return mapping;
        }),
      };
      setFormField(formField);
    }
  };

  const deleteMapping = (mappingToBeDeleted) => {
    const result = reactFlowMapping.filter(
      (edge) =>
        !(
          JSON.stringify(edge.source) === JSON.stringify(mappingToBeDeleted.source) &&
          JSON.stringify(edge.destination) === JSON.stringify(mappingToBeDeleted.destination)
        )
    );
    // setReactFlowMapping(result);
    setReactFlowMapping(result);
    formField = {
      ...formField,
      mappings: formField.mappings.map((mapping) => {
        if (JSON.stringify(mapping.destination_column) === JSON.stringify(mappingToBeDeleted.destination)) {
          return {
            ...mapping,
            source_columns: mapping.source_columns.filter(
              (source) => JSON.stringify(source) !== JSON.stringify(mappingToBeDeleted.source)
            ),
          };
        }
        return mapping;
      }),
    };

    setFormField(formField);
  };

  const onEdgeClick = (event, edge) => {
    if (doubleClickTimeout) {
      clearTimeout(doubleClickTimeout);
      setDoubleClickTimeout(null);
      setEdges((eds) => eds.filter((e) => JSON.stringify(e.source) !== JSON.stringify(edge.source)));
      const source = nodes.find((node) => node.id === edge.source);
      const destination = nodes.find((node) => node.id === edge.target);
      deleteMapping({
        sourceId: edge.source,
        source: { name: source?.data?.label, type: source?.data?.datatype },
        destinationId: edge.target,
        destination: { name: destination?.data?.label, type: destination?.data?.datatype },
      });
    } else {
      setDoubleClickTimeout(setTimeout(() => setDoubleClickTimeout(null), 300));
      setSelectedEdgeId(edge.id);
      setEdgeColorIndex((prev) => {
        const currentIndex = prev[edge.id] || 0;
        return { ...prev, [edge.id]: (currentIndex + 1) % edgeColors.length };
      });
    }
  };

  const getEdgeStyle = (edge) => {
    const colorIndex = edgeColorIndex[edge.id] || 0;
    return {
      stroke: selectedEdgeId === edge.id ? edgeColors[colorIndex] : '#888',
    };
  };

  useEffect(() => {
    transformInputToReactFlow();
    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView();
    }
  }, [formField, disableForm]);

  const transformInputToReactFlow = () => {
    const input = formField.mappings;

    const sourceTableX = 50;
    const destTableX = 400;
    const nodeHeight = 30;

    const nodes = [];
    const edges = [];
    // let sourceIndex = 1;
    // let destinationIndex = 1;
    let sourceY = 0; // Track Y position for source nodes
    let destY = 0; // Track Y position for destination nodes

    // Create table titles
    nodes.push({
      id: 'table1-title',
      position: { x: sourceTableX, y: -nodeHeight + 10 },
      data: { label: 'Source Table' },
      type: 'title',
      draggable: false,
    });

    nodes.push({
      id: 'table2-title',
      position: { x: destTableX, y: -nodeHeight + 10 },
      data: { label: 'Destination Table' },
      draggable: false,
      type: 'title',
    });
    // Add Source Columns

    const maxSourceLength = sourceColumns
      .map((item) => item.header)
      .reduce((max, current) => Math.max(max, current.length), 0);

    sourceColumns.forEach((col) => {
      const sourceNodeId = `source-node-${col.header}`;
      nodes.push({
        id: sourceNodeId,
        position: { x: sourceTableX, y: sourceY },
        data: { label: col.header, datatype: col.cast_datatype, width: maxSourceLength },
        type: 'source',
        draggable: false,
      });
      sourceY += nodeHeight;
    });

    const maxTargetLength = targetColumns
      .map((item) => item.header)
      .reduce((max, current) => Math.max(max, current.length), 0);

    setMaxWidthLength(maxTargetLength);

    targetColumns.forEach((col) => {
      const destNodeId = `target-node-${col.header}`;
      nodes.push({
        id: destNodeId,
        position: { x: destTableX, y: destY },
        data: {
          label: col.header,
          datatype: col.cast_datatype,
          width: maxTargetLength,
          formField,
          setFormField,
          sourceColumns,
          targetColumns,
          reactFlowMapping,
          disableForm,
        },
        type: 'target',
        draggable: false,
      });
      destY += nodeHeight;
    });

    setLastDestY(destY);

    nodes.push({
      id: 'table2-title2',
      position: { x: destTableX, y: destY },
      data: { label: '+', width: maxSourceLength },
      draggable: false,
      type: 'addNode',
    });
    destY += nodeHeight;

    input?.forEach((entry) => {
      const destination = entry.destination_column;
      if (destination.name) {
        const destNodeId = `target-node-${destination.name}`;
        entry.source_columns.forEach((source) => {
          const sourceNodeId = `source-node-${source.name}`;
          const edgeExist = edges.find((e) => e.source === sourceNodeId && e.target === destNodeId);
          if (!edgeExist) {
            edges.push({
              source: sourceNodeId,
              sourceHandle: null,
              target: destNodeId,
              targetHandle: null,
              id: `reactflow__edge-${sourceNodeId}-${destNodeId}`,
              selected: false,
            });
          }

          addNewMapping({
            sourceNodeId,
            source,
            destNodeId,
            destination,
          });
        });
      }
    });

    setNodes(nodes);
    setEdges(edges);
  };

  const proOptions = { hideAttribution: true };

  return (
    <div
      style={{
        height: 500,
        backgroundColor: '#F7F8FB',
        border: '2px solid lightgray',
        borderRadius: '10px',
        position: 'relative',
      }}
      ref={reactFlowWrapper}
    >
      {
        <>
          <Tooltip title="Re-generate by AI">
            <Fab
              aria-label="data-lineage"
              size="small"
              sx={{
                background: '#fff',
                position: 'absolute',
                bottom: 12,
                right: 12,
              }}
              onClick={() => {
                fetchLlmMapperDataByAI();
              }}
            >
              <AutoModeIcon fontSize="medium" color="primary" />
            </Fab>
          </Tooltip>

          <ReactFlowProvider>
            <ReactFlow
              id="llm-mapper-flow"
              nodes={nodes}
              edges={edges.map((edge) => ({
                ...edge,
                style: getEdgeStyle(edge),
              }))}
              onNodesChange={disableForm ? undefined : onNodesChange}
              onEdgesChange={disableForm ? undefined : onEdgesChange}
              onConnect={disableForm ? undefined : onConnect}
              onEdgeClick={disableForm ? undefined : onEdgeClick}
              nodeTypes={nodeTypes}
              fitView
              onLoad={(instance) => {
                reactFlowInstance.current = instance;
              }}
              nodesDraggable={!disableForm}
              nodesConnectable={!disableForm}
              elementsSelectable={!disableForm}
              proOptions={proOptions}
            >
              <Controls />
              <Background />
            </ReactFlow>
          </ReactFlowProvider>
        </>
      }
      {isOpenAddNodeFormModal && (
        <AddColumnForm
          columnForm={columnForm}
          setColumnForm={setColumnForm}
          isModalOpen={isOpenAddNodeFormModal}
          handleCloseModal={handleAddNodeFormClose}
          handleSaveColumnForm={handleSaveColumnForm}
        />
      )}
      {/* <button onClick={handleSave} style={{ marginTop: '10px' }}>
        Save Workflow
      </button> */}
    </div>
  );
};

export default LlmMapperFlow;
